import { defineStore } from 'pinia';
import { serverUrls } from '../api/constants';
import { callAPI } from '@/api/httpservice';
import { useSocketStore } from './socketStore';
import { useUserStore } from './userStore';
import { notify } from '@/utils/toaster';
import i18n from '@/utils/i18n';
import router from '@/router';

export const useRecoStore = defineStore('reco', {
  state: () => ({
    search: '',
    listSearch: '',
    listPage: 1,
    listLimit: 12,
    totalImages: 0,
    // `files` conserve TOUTES les photos renvoyées par le backend, y compris
    // celles marquées `hidden: true` (uploaders masqués). Ces photos sont
    // volontairement envoyées par l'API pour d'autres usages : on les garde en
    // state mais on ne les affiche pas (voir le getter `visibleFiles`).
    // Chaque photo peut porter un champ `hidden?: boolean` (absent === false).
    files: [],
    filesSearch: [],
    selectedFile: null,
    references: [],
    loadingImage: false,
    terminologies: [],
  }),
  getters: {
    // Photos réellement affichables dans la vue Argos : on exclut les photos
    // masquées (`hidden === true`). `hidden` absent/undefined = photo affichée.
    visibleFiles: (state) => state.files.filter((file) => !file.hidden),
  },
  actions: {
    setupRecoListeners() {
      const socketStore = useSocketStore();
      const socket = socketStore.socket;

      if (!socket) {
        console.error('Socket not connected');
        return;
      }

      socket.on('uploaded.photo.created', (data) => {
        // On conserve toujours la photo en state (même masquée), mais on ne
        // comptabilise dans la pagination que les photos réellement affichées.
        this.files.unshift(data);
        if (!data.hidden) {
          this.totalImages += 1;
        }
      });

      socket.on('uploaded.photo.deleted', (data) => {
        this.files = this.files.filter((p) => p._id !== data.photoId);
        this.totalImages = Math.max(0, this.totalImages - 1);
        // On ne recharge la page courante (GET /uploaded-photos) que si
        // l'utilisateur est réellement sur la vue Argos. Sinon le listener,
        // actif en permanence, déclencherait une requête à chaque purge backend.
        if (router.currentRoute.value.name === 'argos') {
          this.getSearchImages();
        }
      });

      socket.on('uploaded.photo.updated', (data) => {
        const index = this.files.findIndex((p) => p._id === data._id);
        if (index !== -1) {
          const wasHidden = !!this.files[index].hidden;
          // On garde la photo en state quel que soit son statut `hidden`.
          this.files.splice(index, 1, data);
          // Si le statut d'affichage a basculé, on réajuste le compteur de
          // pagination (masquée -> visible = +1, visible -> masquée = -1).
          if (wasHidden && !data.hidden) {
            this.totalImages += 1;
          } else if (!wasHidden && data.hidden) {
            this.totalImages = Math.max(0, this.totalImages - 1);
          }
          if (this.selectedFile && this.selectedFile._id === data._id) {
            // Une photo qui devient masquée ne doit plus rester affichée.
            if (data.hidden) {
              this.selectedFile = null;
            } else {
              if (data.description) {
                this.selectedFile.description = data.description;
              }
              this.selectedFile.idList = data.idList;
            }
          }
        }
      });
    },
    getFileUrl(file) {
      if (file) {
        return URL.createObjectURL(file);
      } else {
        return '';
      }
    },
    removeFile(id) {
      this.files = this.files.filter((file) => file._id !== id);
      callAPI('DELETE', `/uploaded-photos/${id}`);
    },
    async sendImage(file, onProgress = null) {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        const formData = new FormData();

        this.loadingImage = true;

        for (const elem of file) {
          formData.append('files', elem);
        }

        const userStore = useUserStore();
        const token = userStore.keycloak?.token;

        xhr.open('POST', serverUrls.authUrl + '/uploaded-photos', true);
        xhr.setRequestHeader('Authorization', 'Bearer ' + token);

        // Récupérer l'avancement de l'upload
        xhr.upload.onprogress = function (event) {
          if (event.lengthComputable) {
            const percentComplete = (event.loaded / event.total) * 100;
            if (onProgress) {
              onProgress(percentComplete, event.loaded, event.total);
            }
          }
        };

        xhr.onreadystatechange = function () {
          this.loadingImage = false;
          console.info('XHR readyState changed:', xhr.readyState, 'Status:', xhr.status);
          if (xhr.readyState === XMLHttpRequest.DONE) {
            if (xhr.status >= 200 && xhr.status < 300) {
              this.listPage = 1;
              this.getSearchImages(); // Assuming this function is available in the context
              resolve(xhr.responseURL);
            } else {
              notify.error(i18n.global.t('upload.send-error'), { description: xhr.responseText.error });
              reject(new Error(xhr.responseText));
            }
          }
        }.bind(this);

        xhr.onerror = function () {
          reject(new Error(`Network error: ${xhr.statusText}`));
          notify.error(i18n.global.t('upload.send-error'), { description: xhr.statusText });
          this.loadingImage = false;
        };

        xhr.send(formData);
        this.loadingImage = false;
      });
    },
    async getSearchImages() {
      await callAPI(
        'GET',
        `/uploaded-photos?page=${this.listPage}&limit=${this.listLimit}&search=${this.listSearch}`,
        null,
      )
        .then((res) => {
          this.files = res.result;
          this.totalImages = res.total;
        })
        .catch((e) => {
          console.error(e);
        });
    },
    async generatePixtralDesc(fileId) {
      return callAPI('POST', `/ia/generate/${fileId}`, null)
        .then((res) => {
          this.files.forEach((file) => {
            if (file._id === fileId) {
              file.pixtralDescription = res.pixtralDesc;
            }
          });
          return res;
        })
        .catch((error) => {
          console.error('Error generating prompt:', error);
          throw error;
        });
    },

    setNextFile() {
      // Navigation uniquement parmi les photos affichées : on ignore les photos
      // masquées présentes en state.
      const files = this.visibleFiles;
      const index = files.findIndex((option) => option._id === this.selectedFile._id);
      if (index !== -1 && index < files.length - 1) {
        this.selectedFile = files[index + 1];
      }
    },

    setPreviousFile() {
      const files = this.visibleFiles;
      const index = files.findIndex((option) => option._id === this.selectedFile._id);
      if (index > 0) {
        this.selectedFile = files[index - 1];
      }
    },

    async fetchTerminologies() {
      // Liste fermée, identique pour toutes les images : on ne la récupère
      // qu'une seule fois puis on la met en cache.
      if (this.terminologies.length) {
        return this.terminologies;
      }
      try {
        const res = await callAPI('GET', '/uploaded-photos/terminologies', null);
        this.terminologies = Array.isArray(res) ? res : [];
      } catch (e) {
        console.error('Error fetching terminologies:', e);
      }
      return this.terminologies;
    },

    async setTerminology(fileId, terminology) {
      try {
        const res = await callAPI('PATCH', `/uploaded-photos/${fileId}/terminology`, { terminology });
        const apply = (file) => {
          if (!file) return;
          file.unknownTerminology = terminology;
          if (res?.description != null) {
            file.description = res.description;
          }
        };
        apply(this.files.find((f) => f._id === fileId));
        if (this.selectedFile?._id === fileId) {
          apply(this.selectedFile);
        }
        return res;
      } catch (error) {
        console.error('Error setting terminology:', error);
        notify.error(i18n.global.t('upload.terminology-error'));
        throw error;
      }
    },

    async generateImsimDesc(fileId) {
      const file = this.files.find((f) => f._id === fileId);
      if (file) {
        file.description = '';
      }
      if (this.selectedFile && this.selectedFile._id === fileId) {
        this.selectedFile.description = '';
      }
      return callAPI('PATCH', `/uploaded-photos/${fileId}`, null).catch((error) => {
        console.error('Error generating prompt:', error);
        throw error;
      });
    },
  },
});
